import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Activity, AlertCircle, CheckCircle2, Clock, RotateCcw, RefreshCw, Eye, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";

const statusIcon = {
  QUEUED: <Clock size={14} className="text-gray-500" />,
  PROCESSING: <Activity size={14} className="text-blue-500 animate-pulse" />,
  POSTED: <CheckCircle2 size={14} className="text-green-500" />,
  FAILED: <AlertCircle size={14} className="text-red-500" />,
  RETRYING: <RotateCcw size={14} className="text-orange-500 animate-spin" />,
};
const statusColor = {
  QUEUED: "bg-gray-100 text-gray-700", PROCESSING: "bg-blue-100 text-blue-800",
  POSTED: "bg-green-100 text-green-800", FAILED: "bg-red-100 text-red-800",
  RETRYING: "bg-orange-100 text-orange-800",
};

export default function AdminSapPage() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sapStatus, setSapStatus] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [retrying, setRetrying] = useState(null);

  useEffect(() => { fetchJobs(); fetchStatus(); }, [statusFilter]);

  const fetchJobs = async () => {
    try {
      const params = { limit: 50 };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/admin/sap-jobs", { params });
      setJobs(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { setJobs([]); }
  };

  const fetchStatus = async () => {
    try { const r = await api.get("/admin/sap-status"); setSapStatus(r.data); } catch { /* ignore */ }
  };

  const handleRetry = async (jobId) => {
    setRetrying(jobId);
    try {
      await api.post(`/admin/sap-jobs/${jobId}/retry`);
      toast.success("Retry succeeded — job posted");
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    }
    setRetrying(null);
  };

  return (
    <div data-testid="admin-sap-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>SAP Integration Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">{total} integration jobs</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => { fetchJobs(); fetchStatus(); }} className="rounded-sm">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] rounded-sm h-9"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="QUEUED">Queued</SelectItem>
              <SelectItem value="PROCESSING">Processing</SelectItem>
              <SelectItem value="POSTED">Posted</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
              <SelectItem value="RETRYING">Retrying</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {sapStatus && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Integration</div>
            <div className="flex items-center gap-2">
              {sapStatus.enabled ? <Wifi size={14} className="text-green-500" /> : <WifiOff size={14} className="text-gray-400" />}
              <span className="text-sm font-semibold">{sapStatus.enabled ? "LIVE" : "MOCK"}</span>
            </div>
          </CardContent></Card>
          <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Service URL</div>
            <div className="text-xs text-gray-600 truncate">{sapStatus.base_url}</div>
          </CardContent></Card>
          <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Company DB</div>
            <div className="text-sm font-medium">{sapStatus.company_db || "—"}</div>
          </CardContent></Card>
          <Card className="border border-gray-200 rounded-sm"><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Max Retries</div>
            <div className="text-sm font-medium">{sapStatus.max_retries}</div>
          </CardContent></Card>
        </div>
      )}

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500 border-b">
                <th className="text-left py-3 px-2">Status</th>
                <th className="text-left py-3 px-2">Ref</th>
                <th className="text-left py-3 px-2">Type</th>
                <th className="text-left py-3 px-2">Entity</th>
                <th className="text-left py-3 px-2">Error</th>
                <th className="text-center py-3 px-2">Retries</th>
                <th className="text-left py-3 px-2">Last Attempt</th>
                <th className="text-right py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-gray-400">No SAP jobs</td></tr>}
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-1.5">
                      {statusIcon[j.status]}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${statusColor[j.status] || ""}`}>{j.status}</span>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-xs font-mono text-[#0052CC]">{j.external_ref_id || "—"}</td>
                  <td className="py-3 px-2 text-xs">{j.job_type}</td>
                  <td className="py-3 px-2 text-xs text-gray-500">{j.entity_type}/{j.entity_id?.slice(0, 8)}</td>
                  <td className="py-3 px-2">
                    {j.error_message ? (
                      <div className="flex items-center gap-1">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${j.error_type === "non_retryable" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                          {j.error_type || "—"}
                        </span>
                        <span className="text-xs text-red-600 max-w-[150px] truncate">{j.error_message}</span>
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-3 px-2 text-center text-xs">{j.retry_count}/{j.max_retries}</td>
                  <td className="py-3 px-2 text-[10px] text-gray-400 whitespace-nowrap">{j.last_attempt_at ? new Date(j.last_attempt_at).toLocaleString() : "—"}</td>
                  <td className="py-3 px-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setDetailJob(j)}>
                        <Eye size={12} />
                      </Button>
                      {(j.status === "FAILED" || j.status === "RETRYING") && (
                        <Button
                          data-testid={`retry-sap-job-${j.id}`}
                          variant="ghost" size="sm"
                          className="h-6 w-6 p-0 text-orange-600"
                          disabled={retrying === j.id}
                          onClick={() => handleRetry(j.id)}
                        >
                          <RotateCcw size={12} className={retrying === j.id ? "animate-spin" : ""} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!detailJob} onOpenChange={() => setDetailJob(null)}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo, sans-serif" }}>SAP Job Detail</DialogTitle></DialogHeader>
          {detailJob && (
            <div className="space-y-4 py-2 max-h-[500px] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block">External Ref</span><span className="font-mono">{detailJob.external_ref_id}</span></div>
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block">Status</span><span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${statusColor[detailJob.status]}`}>{detailJob.status}</span></div>
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block">Error Type</span><span>{detailJob.error_type || "None"}</span></div>
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block">Retries</span><span>{detailJob.retry_count} / {detailJob.max_retries}</span></div>
              </div>
              {detailJob.error_message && (
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">Last Error</span>
                  <pre className="text-xs bg-red-50 border border-red-200 rounded-sm p-3 overflow-x-auto whitespace-pre-wrap">{detailJob.error_message}</pre>
                </div>
              )}
              {detailJob.result && (
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">SAP Result</span>
                  <pre className="text-xs bg-green-50 border border-green-200 rounded-sm p-3 overflow-x-auto">{JSON.stringify(detailJob.result, null, 2)}</pre>
                </div>
              )}
              {detailJob.request_log && (
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">Request Payload (sent to SAP)</span>
                  <pre className="text-xs bg-gray-50 border border-gray-200 rounded-sm p-3 overflow-x-auto">{JSON.stringify(detailJob.request_log, null, 2)}</pre>
                </div>
              )}
              {detailJob.response_log && (
                <div><span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">SAP Response</span>
                  <pre className="text-xs bg-gray-50 border border-gray-200 rounded-sm p-3 overflow-x-auto">{JSON.stringify(detailJob.response_log, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
